import { useEffect, useMemo, useState } from 'react'
import { Program } from '../../types'
import { formatDate, getResource } from '../../utils'

const EpisodeListItem = ({
  episode,
  onClick,
  isInPast,
  hideTitle = false,
  availableOnChannel,
}: {
  episode: {
    id: number
    title: string
    date: Date
    time: string
    number: number
    programTitle?: string
    seriesEpisodeNumber?: number
  }
  isInPast?: boolean
  onClick: () => void
  hideTitle?: boolean
  availableOnChannel: boolean
}) => {
  const programTitle = episode.number
    ? `${getResource('Program.EpisodeAbbreviation')} ${episode.number}`
    : episode.title
  return (
    <button type="button" className={`episode-card`} onClick={onClick}>
      <div className="episode-info">
        {!hideTitle && (
          <div className="episode-title no-translate">{programTitle}</div>
        )}
        <div className="episode-date">
          {formatDate(episode.date)} - {episode.time}
        </div>
      </div>
      <div className="episode-icon">
        {isInPast && availableOnChannel ? (
          <button type="button" className="icon-play-solid">
            <p className="screen-readers-only">
              {getResource('Program.CheckDetail')}
            </p>
          </button>
        ) : (
          <button type="button" className="icon-info">
            <p className="screen-readers-only">
              {getResource('Program.CheckDetail')}
            </p>
          </button>
        )}
      </div>
    </button>
  )
}
export const ChannelEpisodesTabs = ({
  id,
  programTitle,
  seriesEpisodeNumber,
  onProgramClick,
  episodesData,
  isEpisodesDataLoading,
  schedulesData,
  isSchedulesDataLoading,
  utcStartDay,
  availableOnChannel,
}: {
  id: number
  programTitle: string
  seriesEpisodeNumber: number
  onProgramClick: (id: number | undefined) => void
  episodesData: Program[] | null
  isEpisodesDataLoading: boolean
  schedulesData: Program[] | null
  isSchedulesDataLoading: boolean
  availableOnChannel: boolean
  utcStartDay: string
}) => {
  const [activeTab, setActiveTab] = useState<'episodes' | 'schedules'>(
    'episodes'
  )

  const currentUtcDate = new Date().toISOString()

  // Helper to render episode list
  const renderEpisodeList = (data: Program[] | undefined) => {
    if (!data) return null
    return data.map((ep, idx) => (
      <EpisodeListItem
        key={idx}
        episode={{
          id: ep.id,
          title: ep.title,
          date: ep.start.date,
          programTitle,
          seriesEpisodeNumber,
          time: ep.start.time,
          number: ep.seriesEpisodeNumber,
        }}
        isInPast={ep.start.utc < currentUtcDate}
        onClick={() => onProgramClick(ep.id)}
        hideTitle={activeTab === 'schedules'}
        availableOnChannel={availableOnChannel}
      />
    ))
  }
  const filteredSchedulesData = useMemo(
    () => schedulesData?.filter((e) => e.start.utc !== utcStartDay),
    [schedulesData, utcStartDay]
  )
  const filteredEpisodesData = useMemo(
    () => episodesData?.filter((e) => e.id !== id),
    [episodesData, id]
  )

  const hasEpisodesData =
    !!filteredEpisodesData &&
    !isEpisodesDataLoading &&
    filteredEpisodesData.length > 0

  const hasSchedulesData =
    !!filteredSchedulesData &&
    !isSchedulesDataLoading &&
    filteredSchedulesData.length > 0
  useEffect(() => {
    if (!hasEpisodesData && hasSchedulesData) {
      setActiveTab('schedules')
    } else {
      setActiveTab('episodes')
    }
  }, [hasSchedulesData, hasEpisodesData])

  if (!filteredEpisodesData && !filteredSchedulesData) {
    return null
  }
  return (
    <div className="channel-episode-tabs">
      <section
        className="block-tabs fake-tabs bg-color-light"
        data-block-color="gray"
      >
        <div className="part">
          <div className="tab-menu">
            {hasSchedulesData && hasEpisodesData && (
              <ul>
                <li className={`${activeTab === 'episodes' ? ' active' : ''}`}>
                  <a>
                    <button
                      type="button"
                      onClick={() => setActiveTab('episodes')}
                    >
                      {getResource('Program.Drawer.OtherEpisodes')}
                    </button>
                  </a>
                </li>
                <li
                  className={`tab${activeTab === 'schedules' ? ' active' : ''}`}
                >
                  <a>
                    <button
                      type="button"
                      onClick={() => setActiveTab('schedules')}
                    >
                      {getResource('Program.Drawer.OtherSchedules')}
                    </button>
                  </a>
                </li>
              </ul>
            )}
            {hasSchedulesData && !hasEpisodesData && (
              <span className="title-level-6">
                {getResource('Program.Drawer.OtherSchedules')}
              </span>
            )}
            {!hasSchedulesData && hasEpisodesData && (
              <span className="title-level-6">
                {getResource('Program.Drawer.OtherEpisodes')}
              </span>
            )}
          </div>
        </div>
      </section>
      <div className="episodes-list">
        {activeTab === 'schedules' &&
          filteredSchedulesData &&
          renderEpisodeList(filteredSchedulesData)}
        {activeTab === 'episodes' &&
          filteredEpisodesData &&
          renderEpisodeList(filteredEpisodesData)}
      </div>
    </div>
  )
}
